import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderSlot as _renderSlot, isRef as _isRef, withCtx as _withCtx, createVNode as _createVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, renderList as _renderList, createSlots as _createSlots } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("div", null, null, -1)

import { computed } from "vue";
import { SelectorTableProps, TableRow } from "@/types";
import { useTableFilter, useVModel } from "@/hooks";
import { FlexBox } from "@/components/common/display";
import { Searchbar } from "@/components/common/input";
import { IconButton } from "@/components/common/button";
import DataTable from "./DataTable.vue";


/**
 * A data table for selecting items.
 */
const __default__ = {
  name: "SelectorTable",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    selected: {},
    itemName: {},
    addable: { type: Boolean },
    editable: { type: [Boolean, Function] },
    deletable: { type: [Boolean, Function] },
    searchHint: {},
    icons: {},
    columns: {},
    rows: {},
    rowKey: { type: [String, Function] },
    loading: { type: Boolean },
    customCells: {},
    minimal: { type: Boolean }
  },
  emits: ["update:selected", "refresh", "row:edit", "row:delete", "row:add"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const selectedRows = useVModel(props, "selected");

const { searchText, searchLabel, sortBy, sortDesc, filteredRows } =
  useTableFilter(props);

const customCellSlots = computed(() =>
  props.customCells
    ? props.customCells.map((name) => `body-cell-${String(name)}`)
    : []
);

const addLabel = computed(() => `Add ${props.itemName || ""}`);
const editLabel = computed(() => `Edit ${props.itemName || ""}`);
const deleteLabel = computed(() => `Delete ${props.itemName || ""}`);

const addIcon = computed(() => props.icons?.add || "add");
const editIcon = computed(() => props.icons?.edit || "edit");
const deleteIcon = computed(() => props.icons?.delete || "delete");

/**
 * Selects a clicked row, or deselects a selected row.
 * @param evt - The click event.
 * @param row - The row to toggle selecting.
 */
function handleRowClick(evt: Event, row: TableRow): void {
  if (props.selected?.includes(row)) {
    emit("update:selected", []);
  } else {
    emit("update:selected", [row]);
  }
}

/**
 * Whether a row can be edited.
 * @param row - The row to check.
 * @return Whether it is editable.
 */
function isEditEnabled(row: TableRow): boolean {
  return typeof props.editable === "function"
    ? props.editable(row)
    : !!props.editable;
}

/**
 * Whether a row can be deleted.
 * @param row - The row to check.
 * @return Whether it is deletable.
 */
function isDeleteEnabled(row: TableRow): boolean {
  return typeof props.deletable === "function"
    ? props.deletable(row)
    : !!props.deletable;
}

return (_ctx: any,_cache: any) => {
  const _component_q_td = _resolveComponent("q-td")!

  return (_openBlock(), _createBlock(DataTable, {
    "sort-by": _unref(sortBy),
    "onUpdate:sortBy": _cache[5] || (_cache[5] = ($event: any) => (_isRef(sortBy) ? (sortBy).value = $event : null)),
    "sort-desc": _unref(sortDesc),
    "onUpdate:sortDesc": _cache[6] || (_cache[6] = ($event: any) => (_isRef(sortDesc) ? (sortDesc).value = $event : null)),
    selected: _unref(selectedRows),
    "onUpdate:selected": _cache[7] || (_cache[7] = ($event: any) => (_isRef(selectedRows) ? (selectedRows).value = $event : null)),
    columns: props.columns,
    rows: _unref(filteredRows),
    "row-key": props.rowKey,
    "rows-per-page": 5,
    loading: props.loading,
    selection: "single",
    "data-cy": "generic-selector-table",
    "custom-cells": ['actions', ...(props.customCells || [])],
    sort: (r) => r,
    onRowClick: handleRowClick
  }, _createSlots({
    selection: _withCtx(() => [
      _hoisted_1
    ]),
    "body-cell-actions": _withCtx(({ row }) => [
      _createVNode(_component_q_td, {
        onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(FlexBox), { justify: "end" }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "cell-actions", { row: row }),
              (isEditEnabled(row))
                ? (_openBlock(), _createBlock(_unref(IconButton), {
                    key: 0,
                    small: props.minimal,
                    icon: editIcon.value,
                    tooltip: editLabel.value,
                    "data-cy": "button-selector-edit",
                    onClick: ($event: any) => (emit('row:edit', row))
                  }, null, 8, ["small", "icon", "tooltip", "onClick"]))
                : _createCommentVNode("", true),
              (isDeleteEnabled(row))
                ? (_openBlock(), _createBlock(_unref(IconButton), {
                    key: 1,
                    small: props.minimal,
                    icon: deleteIcon.value,
                    tooltip: deleteLabel.value,
                    "data-cy": "button-selector-delete",
                    onClick: ($event: any) => (emit('row:delete', row))
                  }, null, 8, ["small", "icon", "tooltip", "onClick"]))
                : _createCommentVNode("", true)
            ]),
            _: 2
          }, 1024)
        ]),
        _: 2
      }, 1024)
    ]),
    _: 2
  }, [
    (!props.minimal)
      ? {
          name: "top",
          fn: _withCtx(() => [
            _createVNode(_unref(FlexBox), {
              "full-width": "",
              align: "center",
              justify: "between",
              y: "2"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Searchbar), {
                  modelValue: _unref(searchText),
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(searchText) ? (searchText).value = $event : null)),
                  label: _unref(searchLabel),
                  hint: props.searchHint,
                  "data-cy": "input-selector-search",
                  onEnter: _cache[1] || (_cache[1] = ($event: any) => (emit('row:add', _unref(searchText))))
                }, {
                  append: _withCtx(({ search }) => [
                    _renderSlot(_ctx.$slots, "search-append", { search: search })
                  ]),
                  _: 3
                }, 8, ["modelValue", "label", "hint"]),
                _createVNode(_unref(IconButton), {
                  tooltip: "Refresh",
                  icon: "graph-refresh",
                  "data-cy": "button-selector-reload",
                  class: "q-ml-sm",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('refresh')))
                })
              ]),
              _: 3
            })
          ]),
          key: "0"
        }
      : undefined,
    _renderList(customCellSlots.value, (name) => {
      return {
        name: name,
        fn: _withCtx((scope) => [
          _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(scope)))
        ])
      }
    }),
    (!props.minimal && props.addable)
      ? {
          name: "bottom",
          fn: _withCtx(() => [
            _createVNode(_unref(IconButton), {
              fab: "",
              color: "primary",
              icon: addIcon.value,
              tooltip: addLabel.value,
              "data-cy": "button-selector-add",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('row:add', null)))
            }, null, 8, ["icon", "tooltip"]),
            _renderSlot(_ctx.$slots, "bottom")
          ]),
          key: "1"
        }
      : undefined
  ]), 1032, ["sort-by", "sort-desc", "selected", "columns", "rows", "row-key", "loading", "custom-cells", "sort"]))
}
}

})